import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  LessonDTO,
  StreamEventDTO,
  ApiSuccess,
  SetItemVersionDTO,
  ModifyKnowledgeFormDTO,
  CreateSlidesParamsDTO,
  StartEventDTO,
  ProgressEventDTO,
  ChunkEventDTO,
  CompletedEventDTO,
  ErrorEventDTO,
  WorksheetItemType,
  CreateLessonFormDTO,
  GenerateScenarioFormDTO,
  LessonDeltaDTO,
  INSUFFICIENT_CREDITS,
  ELEMENT_POOL_EXHAUSTED,
  EducationLevel,
} from '@superlekcja/shared';
import { ILessonClient, InstructionRejectedError } from './lesson-client';
import { api, getAuthToken, baseURL, getCurrentLanguage } from './api';
import { newAutogenSession, stepAutogenSession, AutogenSession } from '../lesson/autogen-completion';
import { EventSource } from 'eventsource';
import { deepmerge } from 'deepmerge-ts';
import { track } from '../utils/analytics';

enum EventResult {
  COMPLETE,
  CONTINUE
}

type Deferred<T> = {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
}

function deferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void;
  let reject!: (error: unknown) => void;
  const promise = new Promise<T>((res, rej) => { resolve = res; reject = rej });
  return { promise, resolve, reject };
}

/**
 * Odmowa modyfikacji (HTTP 400 z `code`) przychodzi PRZED pierwszym bajtem strumienia, wiec
 * jedynym miejscem, w ktorym da sie ja przeczytac, jest callback `fetch` EventSource'a. Wyjatek
 * rzucony stamtad trafia do listenera 'error' juz bez tresci — dlatego oprocz rzucenia
 * przepuszczamy go przez deferred i metoda klienta odrzuca sie tym samym bledem. Bez tego UI nie
 * ma z czego zbudowac komunikatu przy polu, a nauczyciel widzi cisze albo "Nieoczekiwany blad".
 */
async function responseError(response: Response): Promise<Error> {
  const body = await response.json().catch(() => null) as
    { message?: string, code?: string, reason?: string } | null;
  if (body && typeof body.code === 'string') {
    return new InstructionRejectedError(body.code, body.reason, body.message);
  }
  if (body?.message) {
    return new Error(body.message);
  }
  return new Error(`${response.status} ${response.statusText}`);
}

export class LessonClient implements ILessonClient {
  private readonly _lesson$ = new BehaviorSubject<LessonDTO | null>(null);
  private readonly _events$ = new Subject<StreamEventDTO>();
  private readonly _autogenComplete$ = new Subject<{ topic: string }>();
  private autogenSession: AutogenSession = newAutogenSession(false);
  private lessonRequestedAt = 0;
  /** Scenariusz lekcji z createLesson jeszcze nie gotowy — scenario_ready ma pasc raz, tylko dla nowej lekcji. */
  private scenarioReadyPending = false;
  private _loadingLessonId: string | null = null;

  private activeEventSource: EventSource | null = null;
  private abortController: AbortController | null = null;

  readonly events$: Observable<StreamEventDTO>
  readonly lesson$: Observable<LessonDTO | null>
  readonly autogenComplete$: Observable<{ topic: string }>

  constructor(private readonly handleError: (error: unknown, description: string) => void) {
    this.events$ = this._events$.asObservable();
    this.lesson$ = this._lesson$.asObservable();
    this.autogenComplete$ = this._autogenComplete$.asObservable();
    this._lesson$.subscribe(lesson => {
      if (this.scenarioReadyPending && lesson?.scenario.status?.value === 'completed') {
        this.scenarioReadyPending = false;
        track('scenario_ready', { duration_s: Math.round((Date.now() - this.lessonRequestedAt) / 1000) });
      }
      const fire = stepAutogenSession(this.autogenSession, lesson);
      if (!fire) return;
      const settings = lesson?.scenario.prompt?.settings;
      track('lesson_ready', {
        duration_s: Math.round((Date.now() - this.lessonRequestedAt) / 1000),
        // nazwa zamiast liczby, zeby breakdown w PostHogu byl czytelny (SECONDARY, PRIMARY_4_6...)
        education_level: Object.entries(EducationLevel).find(([, level]) => level === settings?.educationLevel)?.[0],
        grade: settings?.grade,
        lesson_minutes: settings?.duration,
      });
      this._autogenComplete$.next(fire);
    });
  }

  // we do not assume that there's a separate stream for each item.
  // any stream from the backend can carry events for any item.



  private _processSubevent<T>(part: 'scenario' | 'knowledge' | 'slides' | 'worksheet' | 'image', itemId: string | null, subevent: ProgressEventDTO | StartEventDTO | ChunkEventDTO | CompletedEventDTO | ErrorEventDTO) {
    switch (subevent.type) {

      case 'start':
        //subevent.data.id;
        break;

      case 'progress':
        break;

      case 'chunk':

        if (part === 'knowledge' || part === 'scenario') {
          const currentLesson = this._lesson$.getValue();
          if (!currentLesson || !currentLesson[part]) {
            throw new Error('LessonClient: Received chunk but no active lesson or item');
          }
          const aggregated = (currentLesson[part].content?.content || '') + subevent.data;

          this._lesson$.next(applyLessonDelta(currentLesson, {
            [part]: {
              content: {
                content: aggregated
              }
            }
          }));
        }
        break;

      case 'completed':
        return EventResult.COMPLETE;

      case 'error':
        const error = subevent.data;
        //this._updateOperation(operationKey, { status: { value: 'error', message: error.message } });
        return EventResult.COMPLETE;
    }
    return EventResult.CONTINUE;
  }

  /*
  * 
  * shared between scenario/knowledge/worksheet generation;
  * 
  * it is important to recognize the final event and close the event source from the client side
  * to avoid phantom errors and reconnecting.
  * 
  */
  private _processStreamEvent(event: StreamEventDTO): EventResult {
    let currentLesson = this._lesson$.getValue();

    switch (event.type) {

      case 'lesson':
        this._lesson$.next(event.data);
        break;

      case 'lesson-partial':
        if (!currentLesson) {
          throw new Error('LessonClient: Received lesson-partial but no active lesson.');
        }
        this._lesson$.next(applyLessonDelta(currentLesson, event.data as Partial<LessonDTO>));
        // A streaming generation that ran out of credits arrives as a normal
        // error partial carrying the shared INSUFFICIENT_CREDITS sentinel (the
        // real 402 never reaches the client since HTTP 200 + SSE headers were
        // already sent). Detect it generically for every section and fire the
        // top-up dialog — same UX as the non-streaming 402 path. The sentinel
        // stays in the section status; EntityErrorNote maps it for display.
        // Ta sama droga niesie wyczerpana pule elementow (ELEMENT_POOL_EXHAUSTED) — inny
        // sentinel, inny dialog, ten sam mechanizm.
        for (const key of ['scenario', 'knowledge', 'slides', 'image'] as const) {
          const status = (event.data as Partial<LessonDTO>)[key]?.status;
          if (status?.value !== 'error') continue;
          if (status.message === INSUFFICIENT_CREDITS) {
            this.handleError(new Error(INSUFFICIENT_CREDITS), INSUFFICIENT_CREDITS);
          } else if (status.message === ELEMENT_POOL_EXHAUSTED) {
            this.handleError(new Error(ELEMENT_POOL_EXHAUSTED), ELEMENT_POOL_EXHAUSTED);
          }
        }
        break;

      case 'scenario': {
        return this._processSubevent('scenario', null, event.event);
      }

      case 'knowledge': {
        return this._processSubevent('knowledge', null, event.event);
      }

      case 'slides': {
        return this._processSubevent('slides', null, event.event);
      }

      case 'worksheet': {
        return this._processSubevent('worksheet', event.itemId, event.event);
      }

      case 'image': {
        return this._processSubevent('image', null, event.event);
      }

      default:
        break;
    }
    return EventResult.CONTINUE;
  }


  async createLesson(params: CreateLessonFormDTO): Promise<void> {
    this.clearState();
    this.autogenSession = newAutogenSession(true);
    this.scenarioReadyPending = true;
    this.lessonRequestedAt = Date.now();
    const url = `${baseURL}/lessons`;
    // Tworzenie lekcji nie ma pola instrukcji, wiec nie ma komu pokazac odmowy przy polu —
    // ale blad otwarcia strumienia nie moze zniknac po cichu.
    this._generateScenario(url, params).catch(error => this.handleError(error, 'Error creating lesson'));
  }

  async modifyScenario(params: GenerateScenarioFormDTO): Promise<void> {
    const url = `${baseURL}/lessons/${this._lesson$.value?.id}/scenario`
    await this._generateScenario(url, params);
  }

  /*
   * this method is used for both creating a new lesson and modifying an existing scenario;
   * input params are generally different for these two cases; 
   */
  private async _generateScenario(url: string, bodyParams: CreateLessonFormDTO | GenerateScenarioFormDTO) {


    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const token = await getAuthToken()
    const language = getCurrentLanguage()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'Accept-Language': language,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const opened = deferred<void>();

    const es = new EventSource(url, {

      fetch: async (input, init) => {
        try {
          const response = await fetch(input, {
            ...init,
            method: 'POST',
            body: JSON.stringify(bodyParams),
            signal,
            headers: {
              ...init.headers,
              ...headers
            },
          })
          // custom error handling to read detailed error message from backend
          if (!response.ok) {
            throw await responseError(response);
          }
          opened.resolve();
          return response;
        } catch (error) {
          // przerwanie przez klienta to nie awaria — nie ma o czym informowac nauczyciela
          if (signal.aborted) opened.resolve(); else opened.reject(error);
          throw error;
        }
      }
    })

    // closing event source by the server is also treated as an error!
    es.addEventListener('error', (error) => {
      // we need to close to avoid reconnecting
      es.close();

      if (signal.aborted) {
        console.log('Scenario generation aborted by client.');
        // TODO scenario or knowledge!
      } else {
        // Awarie generacji dojezdzaja jako error-partial (patrz _processStreamEvent), a odmowa
        // modyfikacji — przez odrzucenie `opened`. Tu zostaje wylacznie log, bo listener 'error'
        // odpala sie takze na fantomie po zamknieciu strumienia przez serwer.
        console.error('Błąd w scenario generation (fetch lub jego przetworzenie):', error);
      }
    })

    es.addEventListener('message', (event) => {
      const parsed = JSON.parse(event.data) as StreamEventDTO
      const result = this._processStreamEvent(parsed);
      this._events$.next(parsed);
      if (result === EventResult.COMPLETE) {
        es.close();
      }
    })

    // rzuca, gdy backend odrzucil zadanie zanim strumien ruszyl (np. `instruction-rejected`)
    await opened.promise;
  }


  async getLesson(id: string): Promise<void> {
    if (this._loadingLessonId === id || this._lesson$.getValue()?.id === id || this._lesson$.getValue()?.slug === id) {
      console.log('LessonClient: getLesson - lesson already substantially active', id);
      return;
    }
    console.log('LessonClient: getLesson', id);
    // clear previous lesson
    this._lesson$.next(null);
    this.autogenSession = newAutogenSession(false);
    this.scenarioReadyPending = false;

    this.abortCurrentOperation();
    // to avoid double fetching
    this._loadingLessonId = id;

    try {
      // Expect backend to return LessonDTO potentially with slug (even if not in base shared type)
      const response = await api.get<ApiSuccess<LessonDTO>>(`/lessons/${id}`);
      const lesson = response.data.data
      if (lesson.scenario.content?.content) {
        lesson.scenario.content.content = replaceMaterialsLinks(lesson.scenario.content.content, lesson.slug);
      }
      this._lesson$.next(lesson);
    } catch (error: any) {
      this.handleError(error, 'Error getting lesson');
      this._lesson$.next(null);
    } finally {
      this._loadingLessonId = null;
    }
  }



  async updateLesson(lessonUpdate: Partial<LessonDTO['params']>): Promise<void> {
    const lesson = this.requireLesson();
    try {
      const response = await api.post<ApiSuccess<LessonDeltaDTO>>(`/lessons/${lesson.id}`, lessonUpdate);
      const updatedLesson = response.data.data;
      this._lesson$.next(applyLessonDelta(lesson, updatedLesson));
    } catch (error) {
      this.handleError(error, 'Error updating lesson');
    }
  }

  async createOrModifyKnowledge(params: ModifyKnowledgeFormDTO): Promise<void> {

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const token = await getAuthToken()
    const language = getCurrentLanguage()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'Accept-Language': language,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${baseURL}/lessons/${this._lesson$.value!.id}/knowledge`
    const bodyParams: ModifyKnowledgeFormDTO = params.instruction
      ? { instruction: params.instruction }
      : {};

    const opened = deferred<void>();

    const es = new EventSource(url, {
      fetch: async (input, init) => {
        try {
          const response = await fetch(input, {
            ...init,
            method: 'POST',
            body: JSON.stringify(bodyParams),
            signal,
            headers: {
              ...init.headers,
              ...headers
            },
          })
          // custom error handling to read detailed error message from backend
          if (!response.ok) {
            throw await responseError(response);
          }
          opened.resolve();
          return response;
        } catch (error) {
          if (signal.aborted) opened.resolve(); else opened.reject(error);
          throw error;
        }
      }
    })

    es.addEventListener('error', (error) => {
      es.close();
      if (signal.aborted) {
        console.log('Knowledge generation aborted by client.');
      } else {
        // patrz komentarz w _generateScenario — tu wystarczy log
        console.error('Błąd w knowledge generation (fetch lub jego przetworzenie):', error);
      }
    })

    es.addEventListener('message', (event) => {
      const parsed = JSON.parse(event.data) as StreamEventDTO
      const result = this._processStreamEvent(parsed);
      this._events$.next(parsed);
      if (result === EventResult.COMPLETE) {
        es.close();
      }
    })

    // rzuca, gdy backend odrzucil zadanie zanim strumien ruszyl (np. `instruction-rejected`)
    await opened.promise;
  }

  async createOrModifySlides(params: CreateSlidesParamsDTO): Promise<void> {

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const token = await getAuthToken()
    const language = getCurrentLanguage()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'Accept-Language': language,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${baseURL}/lessons/${this._lesson$.value!.id}/slides`
    const bodyParams: CreateSlidesParamsDTO = {
      prompt: {
        instruction: params.prompt.instruction
      }
    };

    const es = new EventSource(url, {
      fetch: async (input, init) => {
        const response = await fetch(input, {
          ...init,
          method: 'POST',
          body: JSON.stringify(bodyParams),
          signal,
          headers: {
            ...init.headers,
            ...headers
          },
        })
        // custom error handling to read detailed error message from backend
        if (!response.ok) {
          const body = await response.json();
          if (body.message) {
            throw new Error(body.message);
          } else {
            throw new Error(`${response.status} ${response.statusText}`);
          }
        }
        return response;
      }
    })

    es.addEventListener('error', (error) => {
      es.close();
      if (signal.aborted) {
        console.log('Slides generation aborted by client.');
      } else {
        // Out-of-credits (and any generation failure) now flows through the
        // error partial in _processStreamEvent, which fires the dialog. The
        // server also closes the stream after sending that partial, which the
        // eventsource polyfill reports here as a phantom 'error' — so we only
        // log it and must NOT raise a toast (that would double-surface / show a
        // spurious "Nieoczekiwany błąd"). Mirrors the knowledge listener.
        console.error('Błąd w slides generation (fetch lub jego przetworzenie):', error);
      }
    })

    es.addEventListener('message', (event) => {
      const parsed = JSON.parse(event.data) as StreamEventDTO
      const result = this._processStreamEvent(parsed);
      this._events$.next(parsed);
      if (result === EventResult.COMPLETE) {
        es.close();
      }
    })
  }

  abortCurrentOperation(): void {
    this._loadingLessonId = null;
    if (this.activeEventSource) {
      this.activeEventSource.close();
      this.activeEventSource = null;
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  clearState(): void {
    this.abortCurrentOperation();
    this._lesson$.next(null);
    this.autogenSession = newAutogenSession(false);
    this.scenarioReadyPending = false;
    console.log('LessonClient: State cleared.');
  }

  // TODO onVersion hook is hacky and the problem is that lesson lives here is propagated with some delay to lesson context :(
  async version(itemType: 'scenario' | 'knowledge' | 'slides' | 'worksheet' | 'image', step: -1 | 1, onVersion: (lesson:LessonDTO)=>void) {
    const lessonId = this._lesson$.value?.id
    if (!lessonId) {
      throw new Error('LessonClient.version - No active lesson to revert/advance version for.');
    }

    try {
      const response = await api.post<ApiSuccess<SetItemVersionDTO>>(
        `/lessons/${lessonId}/${itemType}/version`,
        { step }
      );

      const currentLesson = this._lesson$.getValue();
      if (!currentLesson) throw new Error("Current lesson disappeared while changing version");

      const data = response.data.data;
      let lesson = applyLessonDelta(currentLesson, {
        [data.type]: null // erase the item completely
      });
      lesson = applyLessonDelta(lesson, {[data.type]: data.item});
      this._lesson$.next(lesson);
      onVersion(lesson);
    } catch (e: any) {
      throw e;
    }
  }

  async copy(): Promise<{ id: string, slug: string }> {
    const id = this._lesson$.value?.id;
    if (!id) {
      throw new Error('no active lesson');
    }
    const response = await api.post<ApiSuccess<LessonDTO>>(`/lessons/${id}/copy`);
    const lesson = response.data.data;
    this._lesson$.next(lesson);
    return lesson;
  }

  async buyElements(): Promise<void> {
    const lesson = this._lesson$.value;
    if (!lesson) {
      throw new Error('no active lesson');
    }
    const response = await api.post<ApiSuccess<{ elementsLeft: number }>>(`/lessons/${lesson.id}/elements`);
    // Licznik puli jest w lekcji, wiec podmieniamy go na miejscu — bez przeladowania widoku.
    this._lesson$.next({ ...lesson, elementsLeft: response.data.data.elementsLeft });
  }

  async delete(): Promise<void> {
    const id = this._lesson$.value?.id;
    if (!id) {
      throw new Error('no active lesson');
    }
    await api.delete(`/lessons/${id}`);
    this.clearState();
  }

  private requireLesson() {
    const lesson = this._lesson$.getValue();
    if (!lesson || !lesson.id) {
      throw new Error('lesson not loaded');
    }
    return lesson;
  }


  async generateWorksheetItem(
    materialTemplateIndex: string|null,
    itemIdToReplace: string|null
  ): Promise<void> {
    const lesson = this.requireLesson();
    const token = await getAuthToken()
    const language = getCurrentLanguage()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'Accept-Language': language,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `/api/lessons/${lesson.id}/worksheet/item/${itemIdToReplace ?? ''}`;
    const { signal } = new AbortController();
    const es = new EventSource(url, {
      fetch: async (input, init) => {
        const response = await fetch(input, {
          ...init,
          method: 'POST',
          body: materialTemplateIndex ? JSON.stringify({ materialTemplateIndex }) : JSON.stringify({}),
          signal,
          headers: {
            ...init.headers,
            ...headers
          },
        })
        // custom error handling to read detailed error message from backend
        if (!response.ok) {
          const body = await response.json();
          if (body.message) {
            throw new Error(body.message);
          } else {
            throw new Error(`${response.status} ${response.statusText}`);
          }
        }
        return response;
      }
    })

    es.addEventListener('message', (event) => {
      const parsed = JSON.parse(event.data) as StreamEventDTO
      const result = this._processStreamEvent(parsed);
      this._events$.next(parsed);
      if (result === EventResult.COMPLETE) {
        es.close();
      }
    })
    es.addEventListener('error', (error) => {
      if (es.readyState !== EventSource.CLOSED) {
        console.error('Błąd w worksheet item generation (fetch lub jego przetworzenie)', error);
        es.close();
      }
    })
  }

  async removeWorksheetItem(itemId: string): Promise<void> {
    const lesson = this.requireLesson();
    const response = await api.delete<ApiSuccess<Partial<LessonDTO>>>(`/lessons/${lesson.id}/worksheet/item/${itemId}`);

    if (response.data.status === 'success') {
      this._lesson$.next(applyLessonDelta(lesson, response.data.data));
    }
  }

  async generateImage(): Promise<void> {
    const lesson = this.requireLesson();
    const token = await getAuthToken()
    const language = getCurrentLanguage()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'Accept-Language': language,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // reset progress
    if (lesson.image) {
      this._lesson$.next({...lesson, 
        image: {
          ...lesson.image,
          progress: []
        }
      });
    }

    const url = `/api/lessons/${lesson.id}/image`;
    const { signal } = new AbortController();
    const es = new EventSource(url, {
      fetch: async (input, init) => {
        const response = await fetch(input, {
          ...init,
          method: 'POST',
          body: JSON.stringify({ }),
          signal,
          headers: {
            ...init.headers,
            ...headers
          },
        })
        // custom error handling to read detailed error message from backend
        if (!response.ok) {
          const body = await response.json();
          if (body.message) {
            throw new Error(body.message);
          } else {
            throw new Error(`${response.status} ${response.statusText}`);
          }
        }
        return response;
      }
    })

    es.addEventListener('message', (event) => {
      const parsed = JSON.parse(event.data) as StreamEventDTO
      const result = this._processStreamEvent(parsed);
      this._events$.next(parsed);
      if (result === EventResult.COMPLETE) {
        es.close();
      }
    })
    es.addEventListener('error', (error) => {
      if (es.readyState !== EventSource.CLOSED) {
        console.error('Błąd w worksheet item generation (fetch lub jego przetworzenie)', error);
        es.close();
      }
    })
  }
}


function applyLessonDelta(lesson: LessonDTO, update: LessonDeltaDTO): LessonDTO {
  const base = deepmerge({}, lesson)
  if (update.worksheet?.items_ids && base.worksheet?.items_ids) {
    base.worksheet.items_ids = []
  }
  if (update.history?.scenario) {
    base.history = {
      ...base.history,
      scenario: []
    }
  }
  if (update.history?.knowledge) {
    base.history = {
      ...base.history,
      knowledge: []
    }
  }
  if (update.history?.worksheet) {
    base.history = {
      ...base.history,
      worksheet: []
    }
  }
  if (update.scenario?.content?.content) {
    update.scenario.content.content = replaceMaterialsLinks(update.scenario.content.content, base.slug);
  }
  // deepmerge-ts concatenates arrays; a refine's completed partial replaces
  // slides.content (the deck with its slides[] array) wholesale, so clear the
  // previous deck content before merge to avoid duplicated/appended slides.
  if (update.slides?.content && base.slides?.content) {
    base.slides.content = null;
  }

  const result = deepmerge(base, update);


  console.log('applyLessonDelta', result);
  return result;
}


function replaceMaterialsLinks(content: string, lessonId: string) {
  const targetUrl = `/lessons/${lessonId}/worksheet/`
  return content.replace(/\(\/materials\/(\d+)\)/g, `(${targetUrl})`);
}